import { DEFAULT_VOICE, type BrandVoice } from "@gf/shared";
import { Button, Callout, Card, Field, IconButton, Input, Skeleton, TagInput, Textarea, useToast } from "@gf/ui";
import { Plus, Sparkles, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { IgScopeNote } from "../../components/accounts/IgScopeNote";
import { SaveBar, useUnsavedChangesGuard } from "../../components/SaveBar";
import { useSaveSetting, useSettings, useSettingsScope } from "./useSettings";

const MAX_EXAMPLES = 8;
const TONE_SUGGESTIONS = ["friendly, confident, straight to the point", "warm and encouraging", "bold, funny, a little cheeky", "calm, expert, no hype"];

function sameJson(a: unknown, b: unknown) {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function VoiceTab() {
  const settings = useSettings();
  const { igAccountId } = useSettingsScope();
  if (settings.isPending) return <Skeleton height={520} radius={10} />;
  if (settings.isError) {
    return (
      <Callout tone="danger" title="Couldn't load your brand voice" action={<Button size="sm" onClick={() => void settings.refetch()}>Try again</Button>}>
        {settings.error instanceof Error ? settings.error.message : null}
      </Callout>
    );
  }
  // Keyed by account: each Instagram account has its own voice, and a draft never crosses over.
  return <VoiceForm key={igAccountId ?? "user"} initial={settings.data.voice} />;
}

function VoiceForm({ initial }: { initial: BrandVoice }) {
  const toast = useToast();
  const save = useSaveSetting<BrandVoice>("voice");
  const [draft, setDraft] = useState<BrandVoice>(initial);
  const [saved, setSaved] = useState<BrandVoice>(initial);

  useEffect(() => {
    setSaved(initial);
    setDraft((d) => (sameJson(d, saved) ? initial : d));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  // Ignore empty example boxes when comparing / saving.
  const clean = (v: BrandVoice): BrandVoice => ({ ...v, examples: v.examples.map((e) => e.trim()).filter(Boolean) });
  const dirty = !sameJson(clean(draft), clean(saved));
  useUnsavedChangesGuard(dirty);

  function patch(p: Partial<BrandVoice>) {
    setDraft((d) => ({ ...d, ...p }));
  }

  const linkInvalid = !!draft.link.trim() && !/^https?:\/\/\S+\.\S+/i.test(draft.link.trim());

  async function onSave() {
    if (linkInvalid) return;
    try {
      const res = await save.mutateAsync(clean(draft));
      const next = { ...DEFAULT_VOICE, ...res.value };
      setSaved(next);
      setDraft(next);
      toast.success("Brand voice saved", "Every AI message from now on uses it.");
    } catch (err) {
      toast.error(err);
    }
  }

  const examples = draft.examples.length ? draft.examples : [""];

  return (
    <div className="gf-stack gfp-voice">
      <IgScopeNote subject="Brand voice" />
      <Callout tone="neutral" icon={<Sparkles size={18} />} title="This is the voice behind every AI message">
        DMs, comment replies, follow-ups and captions all read this before they write a word. The more specific you are here,
        the less GramFather sounds like a robot and the more it sounds like you.
      </Callout>

      <div className="gfp-settings-grid">
        <Card title="The business" subtitle="What you do and who it's for.">
          <div className="gf-stack">
            <Field label="Business or brand name">
              <Input value={draft.businessName} onChange={(e) => patch({ businessName: e.target.value })} placeholder="Vaughn Fitness" maxLength={120} />
            </Field>
            <Field label="What you sell" hint="One or two sentences. Plain English beats a slogan.">
              <Textarea
                rows={3}
                autoGrow
                value={draft.whatYouSell}
                onChange={(e) => patch({ whatYouSell: e.target.value })}
                placeholder="12-week online coaching for busy dads who want to lose 20 lbs without giving up beer."
                maxLength={600}
              />
            </Field>
            <Field label="Ideal customer" hint="Who should GramFather be talking to?">
              <Textarea
                rows={3}
                autoGrow
                value={draft.idealCustomer}
                onChange={(e) => patch({ idealCustomer: e.target.value })}
                placeholder="Men 30 to 50 with kids and a desk job, who've tried diets before and quit."
                maxLength={600}
              />
            </Field>
          </div>
        </Card>

        <Card title="How you sound" subtitle="Tone, the ask, and how you sign off.">
          <div className="gf-stack">
            <Field label="Tone">
              <Input value={draft.tone} onChange={(e) => patch({ tone: e.target.value })} placeholder={DEFAULT_VOICE.tone} maxLength={160} />
            </Field>
            <div className="gfp-chips" aria-label="Tone suggestions">
              {TONE_SUGGESTIONS.map((t) => (
                <button key={t} type="button" className="gfp-chip" aria-pressed={draft.tone === t} onClick={() => patch({ tone: t })}>
                  {t}
                </button>
              ))}
            </div>
            <Field label="What should people do next?" hint={`The call to action, e.g. "book a free call" or "grab the free guide".`}>
              <Input value={draft.callToAction} onChange={(e) => patch({ callToAction: e.target.value })} placeholder="book a free 15-minute call" maxLength={160} />
            </Field>
            <Field label="Link" error={linkInvalid ? "Use a full link starting with https://" : undefined}>
              <Input type="url" value={draft.link} onChange={(e) => patch({ link: e.target.value })} placeholder="https://yourbrand.com/call" maxLength={300} />
            </Field>
            <Field label="Sign-off">
              <Input value={draft.signOff} onChange={(e) => patch({ signOff: e.target.value })} placeholder="Talk soon, Jon" maxLength={80} />
            </Field>
          </div>
        </Card>
      </div>

      <Card
        title="Examples in your own words"
        subtitle="Paste a few real messages you've sent. GramFather copies the rhythm, not the words."
        actions={
          <Button
            size="sm"
            variant="ghost"
            icon={<Plus size={15} />}
            disabled={draft.examples.length >= MAX_EXAMPLES}
            onClick={() => patch({ examples: [...examples, ""] })}
          >
            Add example
          </Button>
        }
      >
        <ol className="gfp-examples">
          {examples.map((ex, i) => (
            <li key={i} className="gfp-examples__item">
              <Textarea
                rows={2}
                autoGrow
                value={ex}
                aria-label={`Example message ${i + 1}`}
                placeholder={i === 0 ? "Hey! Saw you're into home workouts too. What's been the hardest part of staying consistent?" : "Another message you've sent…"}
                onChange={(e) => {
                  const next = [...examples];
                  next[i] = e.target.value;
                  patch({ examples: next });
                }}
                maxLength={1000}
              />
              <IconButton
                label="Remove example"
                icon={<Trash2 />}
                size="sm"
                disabled={examples.length === 1 && !ex}
                onClick={() => patch({ examples: examples.filter((_, idx) => idx !== i) })}
              />
            </li>
          ))}
        </ol>
      </Card>

      <Card title="Never say" subtitle="Words, phrases and claims the AI must never use. Press Enter after each one.">
        <Field label="Banned words and claims">
          <TagInput
            value={draft.avoid}
            onChange={(v) => patch({ avoid: v })}
            placeholder={`e.g. "guaranteed", "DM me 'INFO'", "bro"`}
            max={50}
          />
        </Field>
      </Card>

      <SaveBar dirty={dirty} saving={save.isPending} disabled={linkInvalid} onSave={() => void onSave()} onDiscard={() => setDraft(saved)} />
    </div>
  );
}
